import '../styles/GlobalDesign.css';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const BTN = (bg, color = '#fff') => ({
  padding: '8px 16px', background: bg, border: 'none', borderRadius: '8px',
  color, fontWeight: '600', cursor: 'pointer', fontSize: '0.83rem', whiteSpace: 'nowrap',
});

const INPUT_STYLE = {
  padding: '9px 12px', background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
  color: '#F1F5F9', fontSize: '0.9rem', width: '180px', boxSizing: 'border-box',
};

const CARD_STYLE = {
  background: '#1E293B', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '16px', padding: '28px',
};

const AdminResellerSettings = () => {
  const [settings, setSettings]   = useState({ membership_price: 2000, membership_auto_activate: false });
  const [priceInput, setPriceInput] = useState('2000');
  const [autoActivate, setAutoActivate] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');

  const [resellers, setResellers]     = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [toggling, setToggling]       = useState(null); // reseller id being toggled
  const [error, setError]             = useState('');
  const [searchTerm, setSearchTerm]   = useState('');

  const token = () => localStorage.getItem('token');

  // ── Load settings ──────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/membership-settings`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setSettings(res.data);
      setPriceInput(String(res.data.membership_price));
      setAutoActivate(res.data.membership_auto_activate);
    } catch {
      setError('Impossible de charger les paramètres');
    }
  }, []);

  // ── Load resellers list ────────────────────────────────────────────
  const loadResellers = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/admin/resellers`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setResellers(res.data.resellers || []);
    } catch {
      setError('Impossible de charger la liste des revendeurs');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadResellers();
  }, [loadSettings, loadResellers]);

  // ── Save settings ──────────────────────────────────────────────────
  const saveSettings = async () => {
    const price = parseFloat(priceInput);
    if (isNaN(price) || price < 0) {
      alert('Veuillez entrer un prix valide (≥ 0)');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      await axios.put(
        `${API_URL}/admin/membership-settings`,
        { membership_price: price, membership_auto_activate: autoActivate },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setSaveMsg('✅ Paramètres enregistrés');
      setSettings({ membership_price: price, membership_auto_activate: autoActivate });
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle membership for a single reseller ────────────────────────
  const toggleMembership = async (reseller) => {
    const newValue = !reseller.membership_paid;
    setToggling(reseller.id);
    try {
      await axios.put(
        `${API_URL}/admin/resellers/${reseller.id}/membership`,
        { membership_paid: newValue },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setResellers(prev =>
        prev.map(r =>
          r.id === reseller.id
            ? { ...r, membership_paid: newValue ? 1 : 0 }
            : r
        )
      );
    } catch {
      alert('Erreur lors de la modification de l\'adhésion');
    } finally {
      setToggling(null);
    }
  };

  // ── Activate all resellers at once ─────────────────────────────────
  const activateAll = async () => {
    if (!window.confirm('Activer l\'adhésion pour TOUS les revendeurs non-adhérents ?')) return;
    const targets = resellers.filter(r => !r.membership_paid);
    for (const r of targets) {
      try {
        await axios.put(
          `${API_URL}/admin/resellers/${r.id}/membership`,
          { membership_paid: true },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
      } catch { /* continue */ }
    }
    loadResellers();
  };

  // ── Filter ─────────────────────────────────────────────────────────
  const filtered = resellers.filter(r => {
    const term = searchTerm.toLowerCase();
    return (
      !term ||
      (r.business_name || '').toLowerCase().includes(term) ||
      (r.email || '').toLowerCase().includes(term)
    );
  });

  const unpaidCount = resellers.filter(r => !r.membership_paid).length;

  return (
    <AdminLayout>
      <div style={{ padding: '24px', maxWidth: '1100px' }}>
        {/* Page title */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ color: '#F1F5F9', fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
            ⚙️ Paramètres d'adhésion revendeur
          </h1>
          <p style={{ color: '#64748B', fontSize: '0.88rem', marginTop: '6px' }}>
            Configurez le prix d'adhésion et les règles d'activation pour les revendeurs.
          </p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px', padding: '12px 16px', color: '#F87171', marginBottom: '20px',
          }}>
            {error}
          </div>
        )}

        {/* ── Settings card ── */}
        <div style={{ ...CARD_STYLE, marginBottom: '28px' }}>
          <h2 style={{ color: '#F1F5F9', fontSize: '1rem', fontWeight: '700', marginTop: 0, marginBottom: '24px' }}>
            💳 Configuration globale
          </h2>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', alignItems: 'flex-end' }}>
            {/* Price input */}
            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.82rem', marginBottom: '8px', fontWeight: '600' }}>
                Prix d'adhésion (FCFA)
              </label>
              <input
                type="number"
                min="0"
                value={priceInput}
                onChange={e => setPriceInput(e.target.value)}
                style={INPUT_STYLE}
              />
              <div style={{ color: '#475569', fontSize: '0.75rem', marginTop: '4px' }}>
                Actuellement: <strong style={{ color: '#94A3B8' }}>{settings.membership_price.toLocaleString()} FCFA</strong>
              </div>
            </div>

            {/* Auto-activate toggle */}
            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.82rem', marginBottom: '8px', fontWeight: '600' }}>
                Activation automatique à l'inscription
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  onClick={() => setAutoActivate(v => !v)}
                  style={{
                    width: '48px', height: '26px', borderRadius: '13px', cursor: 'pointer',
                    background: autoActivate ? '#22C55E' : 'rgba(255,255,255,0.12)',
                    position: 'relative', transition: 'background 0.2s',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '3px',
                    left: autoActivate ? '25px' : '3px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: '#fff', transition: 'left 0.2s',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  }} />
                </div>
                <span style={{ color: autoActivate ? '#22C55E' : '#64748B', fontSize: '0.85rem', fontWeight: '600' }}>
                  {autoActivate ? 'Activé' : 'Désactivé'}
                </span>
              </div>
              <div style={{ color: '#475569', fontSize: '0.75rem', marginTop: '4px' }}>
                {autoActivate
                  ? 'Les nouveaux revendeurs sont adhérents dès l\'inscription'
                  : 'Validation manuelle requise à l\'inscription'}
              </div>
            </div>

            {/* Save button */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button
                onClick={saveSettings}
                disabled={saving}
                style={BTN(saving ? '#334155' : '#3B82F6')}
              >
                {saving ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </button>
              {saveMsg && (
                <span style={{
                  fontSize: '0.78rem',
                  color: saveMsg.startsWith('✅') ? '#22C55E' : '#F87171',
                }}>
                  {saveMsg}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Resellers list ── */}
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
            <div>
              <h2 style={{ color: '#F1F5F9', fontSize: '1rem', fontWeight: '700', margin: 0 }}>
                🤝 Adhésion des revendeurs
              </h2>
              <p style={{ color: '#475569', fontSize: '0.78rem', margin: '4px 0 0' }}>
                {resellers.length} revendeurs · {unpaidCount} sans adhésion
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ ...INPUT_STYLE, width: '200px' }}
              />
              {unpaidCount > 0 && (
                <button onClick={activateAll} style={BTN('rgba(34,197,94,0.2)', '#22C55E')}>
                  ✅ Activer tous ({unpaidCount})
                </button>
              )}
            </div>
          </div>

          {loadingList ? (
            <div style={{ textAlign: 'center', color: '#64748B', padding: '40px' }}>
              ⏳ Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#64748B', padding: '40px' }}>
              Aucun revendeur trouvé.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Revendeur', 'Email', 'Statut', 'Adhésion', 'Inscription', 'Action'].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', color: '#64748B', fontSize: '0.78rem',
                        fontWeight: '700', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const isPaid = Boolean(r.membership_paid);
                    return (
                      <tr
                        key={r.id}
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                        }}
                      >
                        {/* Name */}
                        <td style={{ padding: '12px 14px', color: '#F1F5F9', fontSize: '0.88rem', fontWeight: '600' }}>
                          {r.business_name || r.contact_name || `#${r.id}`}
                        </td>
                        {/* Email */}
                        <td style={{ padding: '12px 14px', color: '#94A3B8', fontSize: '0.83rem' }}>
                          {r.email}
                        </td>
                        {/* Account status */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                            background: r.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                            color: r.status === 'active' ? '#22C55E' : '#F87171',
                          }}>
                            {r.status === 'active' ? 'Actif' : 'Suspendu'}
                          </span>
                        </td>
                        {/* Membership */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                            background: isPaid ? 'rgba(59,130,246,0.15)' : 'rgba(100,116,139,0.15)',
                            color: isPaid ? '#60A5FA' : '#64748B',
                          }}>
                            {isPaid ? '✅ Adhérent' : '⏳ En attente'}
                          </span>
                        </td>
                        {/* Created at */}
                        <td style={{ padding: '12px 14px', color: '#475569', fontSize: '0.78rem' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        {/* Toggle button */}
                        <td style={{ padding: '12px 14px' }}>
                          <button
                            onClick={() => toggleMembership(r)}
                            disabled={toggling === r.id}
                            style={BTN(
                              isPaid ? 'rgba(239,68,68,0.18)' : 'rgba(34,197,94,0.18)',
                              isPaid ? '#F87171' : '#22C55E'
                            )}
                          >
                            {toggling === r.id
                              ? '⏳'
                              : isPaid
                                ? '❌ Désactiver'
                                : '✅ Activer'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminResellerSettings;
